import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Quote,
  selectClient,
  selectSession,
  selectSessionAdvisor,
  selectIllustration,
  putMultipleFundingSourceForClientByClientIdRequest,
} from '@/model';
import { SessionType } from '@shared';
import {
  createPolicyFundingSourceFromQuote,
  createUpdatedQuoteFromResponse,
  createQuoteWithROIandProjectedCoverage,
} from '@/util/policyQuoteCalculations';
import {
  deleteJointPolicyFromBothClients,
  findPartnerPolicyFundingSourceId,
} from '@/util/findPartnerPolicyFundingSourceId';
import type { QuoteWithROI } from '@/features/shop-insurance';

interface UsePolicyManagementProps {
  quote: Quote;
  selectedTab?: ShopInsuranceTab;
  clientPartner?: Client | null;
  fundingSourceId?: string | null;
  onClose?: (extraParams?: Record<string, string>) => void;
}

export function usePolicyManagement({
  quote,
  selectedTab = 'single',
  clientPartner = null,
  fundingSourceId = null,
  onClose,
}: UsePolicyManagementProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const illustration = useSelector(selectIllustration);
  const existingUpdatedQuote = quote.updatedQuote || null;
  const illustrationPdfUrl = quote.illustrationPdfUrl || null;

  const quoteId = quote.insuranceQuoteId;
  const completedIllustration = illustration.completedIllustrations?.[quoteId];

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const [isSaving, setIsSaving] = useState(false);

  const handleAddPolicy = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!client) return;

    setIsSaving(true);

    let quoteToUse = quote;
    let updatedQuoteForStorage: Quote | null = null;
    let finalIllustrationPdfUrl = illustrationPdfUrl;

    if (completedIllustration?.responseData && completedIllustration?.downloaded) {
      const updatedQuote = createUpdatedQuoteFromResponse(
        quote,
        completedIllustration.responseData,
      );

      const originalROI = (quote as QuoteWithROI).originalROIValues;

      if (
        originalROI &&
        originalROI.projectedReturnOnInvestment !== undefined &&
        originalROI.projectedCompoundAnnualGrowthRate !== undefined &&
        originalROI.investmentPeriodYears !== undefined
      ) {
        updatedQuoteForStorage = {
          ...updatedQuote,
          roiValues: {
            projectedReturnOnInvestment: originalROI.projectedReturnOnInvestment,
            projectedCompoundAnnualGrowthRate: originalROI.projectedCompoundAnnualGrowthRate,
            projectedInternalRateOfReturn: originalROI.projectedInternalRateOfReturn,
            investmentPeriodYears: originalROI.investmentPeriodYears,
          },
        } as Quote;
      } else {
        updatedQuoteForStorage = createQuoteWithROIandProjectedCoverage(updatedQuote, client);
      }

      quoteToUse = updatedQuote;
      finalIllustrationPdfUrl = completedIllustration.pdfUrl;
    } else if ((quote as QuoteWithROI).originalROIValues) {
      quoteToUse = quote;
      updatedQuoteForStorage = quote;
    } else if (existingUpdatedQuote) {
      quoteToUse = existingUpdatedQuote;
      updatedQuoteForStorage = existingUpdatedQuote;
    } else {
      updatedQuoteForStorage = createQuoteWithROIandProjectedCoverage(quote, client);
    }

    const finalPolicy = createPolicyFundingSourceFromQuote(
      quoteToUse,
      client,
      isAdvisorSession,
      advisor?.advisorId,
      updatedQuoteForStorage,
      finalIllustrationPdfUrl,
    );

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: finalPolicy,
        fundingSourceId: null,
      }),
    );

    if (selectedTab === 'joint' && quote.policyType === 'Joint Life' && clientPartner) {
      const partnerFinalPolicy = createPolicyFundingSourceFromQuote(
        quoteToUse,
        clientPartner,
        isAdvisorSession,
        advisor?.advisorId,
        updatedQuoteForStorage,
        finalIllustrationPdfUrl,
      );

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: clientPartner.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: partnerFinalPolicy,
          fundingSourceId: null,
        }),
      );
    }
  };

  const deletePolicyForClientOnly = () => {
    if (!client || !fundingSourceId) return;

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: null,
        fundingSourceId,
      }),
    );
  };

  const deletePolicyForBothClients = () => {
    if (!client || !fundingSourceId) return;

    deleteJointPolicyFromBothClients(
      dispatch,
      client,
      fundingSourceId,
      putMultipleFundingSourceForClientByClientIdRequest,
    );
  };

  const handleRemovePolicy = (event: React.MouseEvent, onShowJointDialog: () => void) => {
    event.stopPropagation();

    if (!client || !fundingSourceId) return;

    setIsSaving(true);

    if (
      selectedTab === 'joint' &&
      quote.policyType === 'Joint Life' &&
      clientPartner &&
      quote.insuranceQuoteId
    ) {
      const partnerFundingSourceId = findPartnerPolicyFundingSourceId(clientPartner, {
        insuranceQuoteId: quote.insuranceQuoteId,
      });

      if (partnerFundingSourceId) {
        onShowJointDialog();
      } else {
        deletePolicyForClientOnly();
      }
    } else {
      deletePolicyForClientOnly();
    }
  };

  return {
    handleAddPolicy,
    handleRemovePolicy,
    deletePolicyForClientOnly,
    deletePolicyForBothClients,
    isSaving,
  };
}
